import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  School as SchoolIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useGetClassAssignedExamsQuery } from '@/store/api/adminApi';

const ClassAssignedExamsDialog = React.memo(({ open, onClose, classData }) => {
  const {
    data: assignedExamsData,
    isLoading,
    error,
  } = useGetClassAssignedExamsQuery(classData?._id, {
    skip: !classData?._id || !open,
  });

  const assignedExams = useMemo(() => assignedExamsData?.data?.assignments || [], [assignedExamsData?.data]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SchoolIcon sx={{ color: '#2196F3', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Assigned Exams
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {classData?.name} ({classData?.classCode})
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load assigned exams. Please try again.
          </Alert>
        ) : assignedExams.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <QuizIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Exams Assigned
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This class has no exams assigned yet.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
              {assignedExams.length} exam{assignedExams.length !== 1 ? 's' : ''} assigned to this class:
            </Typography>
            
            <List sx={{ bgcolor: 'background.paper' }}>
              {assignedExams.map((exam, index) => (
                <React.Fragment key={exam._id}>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      border: '1px solid rgba(0,0,0,0.08)',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <QuizIcon sx={{ color: '#4CAF50' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="600">
                          {exam.exam?.title}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {exam.exam?.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            <Chip
                              label={new Date(exam.assignedAt).toLocaleDateString('vi-VN')}
                              size="small"
                              sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < assignedExams.length - 1 && <Divider variant="middle" />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          startIcon={<CloseIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

ClassAssignedExamsDialog.displayName = 'ClassAssignedExamsDialog';

export default ClassAssignedExamsDialog;
